import React, { useEffect, useState } from 'react'
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs'
import Hero from '../../components/Hero/Hero'
import SearchBar from '../../components/SearchBar/SearchBar'
import Filters, { ICategoryProps } from '../../components/Filters/Filters'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { getActivities, getCategories } from '../../redux/slices/activities/activitiesThunk'
import Loading from '../../components/Loading/Loading'
import Pagination from '../../components/Pagination/Pagination'
import { useLocation, useNavigate } from 'react-router-dom'
import ResponsiveContainer from '../../layouts/ResponsiveContainer'
import SEO from '../../components/SEO/SEO'
import { clearActivities } from '../../redux/slices/activities/activitiesSlice'
import ActivitiesMapper from '../../components/Mappers/ActivitiesMapper'
import ContactSection from '../../components/Common/ContactSection'

const DEFAULT_MIN_DATE = '2024-01-01';

const Index = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);

    // Obtención de datos del estado global de Redux
    const { activities, pagination, loading, categoriesOptions } = useAppSelector((state) => state.activities)
    const { currentPage, totalPages, totalItems } = pagination;

    // Estados locales para filtros y fechas
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [categorieSelected, setCategorieSelected] = useState<string>('');
    const [dateFrom, setDateFrom] = useState<string>('');
    const [dateTo, setDateTo] = useState<string>('');
    const [disabledPeriodFrom, _setDisabledPeriodFrom] = useState<boolean>(false);
    const [disabledPeriodTo, setDisabledPeriodTo] = useState<boolean>(true);
    const [minPeriodDateFrom, _setMinPeriodDateFrom] = useState<string>(DEFAULT_MIN_DATE);
    const [minPeriodDateTo, setMinPeriodDateTo] = useState<string>('');
    const [maxPeriodDateFrom, setMaxPeriodDateFrom] = useState<string>('');
    const [maxPeriodDateTo, setMaxPeriodDateTo] = useState<string>('');
    const [disabledSearch, setDisabledSearch] = useState<boolean>(true);
    const [cleanValue, setCleanValue] = useState<boolean>(false);

    // Efecto para cargar categorías y establecer límite de fecha máximo
    useEffect(() => {
        if (!categoriesOptions?.length) {
            dispatch(getCategories())
        }
        maxDateLimit();
    }, [categoriesOptions?.length])

    // Efecto para manejar cambios en la URL y actualizar filtros
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const query = params.get('search') || '';
        const page = parseInt(params.get('page') || '1', 10);
        const categorie = params.get('categorie') || '';
        const dateTo = params.get('to') || "";
        const dateFrom = params.get('from') || "";

        // Actualización de los estados con los parámetros de la URL
        if (categorie) setCategorieSelected(categorie);
        if (dateFrom) setDateFrom(dateFrom);
        if (dateTo) { setDateTo(dateTo); setDisabledPeriodTo(false) };
        if (query) { setSearchQuery(query); setCleanValue(true) };

        // Despacho de la acción para obtener actividades basadas en los parámetros de la URL
        dispatch(getActivities({ categoria: categorie, page, search: query, fechaTo: dateTo, fechaFrom: dateFrom }));
    }, [dispatch, location.search]);

    useEffect(() => {
        return () => {
            dispatch(clearActivities())
        }
    }, [])

    // Manejo de cambio de página en la paginación
    const handlePageChange = (page: number) => {
        const params = new URLSearchParams(location.search);
        searchQuery && params.set('search', searchQuery);
        params.set('page', page.toString());
        navigate({ search: params.toString() });
    };

    // Manejo de búsqueda
    const handleSearch = (query: string) => {
        const params = new URLSearchParams(location.search);
        setSearchQuery(query);
        setCleanValue(true);
        query ? params.set('search', query) : params.delete('search');
        params.set('page', '1');
        navigate({ search: params.toString() });
    };

    // Manejo de selección de categoría
    const onSelect = (value: string) => {
        const params = new URLSearchParams(location.search);
        setCategorieSelected(value !== 'Todas' ? value : '');
        value !== 'Todas' ? params.set('categorie', value) : params.delete('categorie');
        params.set('page', '1');
        navigate({ search: params.toString() });
    };

    // Manejo de cambio en la fecha "Desde"
    const onHandleChangePeriodFrom = (e: React.ChangeEvent<HTMLInputElement>) => {
        const date = e.target.value;
        const params = new URLSearchParams(location.search);
        if (date === "") {
            setDisabledPeriodTo(true);
            setDateTo('');
            setDateFrom('');
            params.delete('from');
            params.delete('to');
        } else {
            setDateFrom(date);
            setDateTo('');
            setDisabledPeriodTo(false);
            setMinPeriodDateTo(e.target.value)
        }
        navigate({ search: params.toString() });
    }

    // Manejo de cambio en la fecha "Hasta"
    const onHandleChangePeriodTo = (e: React.ChangeEvent<HTMLInputElement>) => {
        const dateTo = e.target.value;
        if (dateTo !== '') {
            setDateTo(e.target.value);
            params.set('from', dateFrom);
            params.set('to', dateTo);
            params.set('page', '1');
        } else {
            setDateTo('');
            setDateFrom('');
            params.delete('from');
            params.delete('to');
        }
        navigate({ search: params.toString() });
    }

    // Función para establecer el límite máximo de la fecha al día actual
    const maxDateLimit = () => {
        const currentDay = new Date().toISOString().slice(0, 10);
        setMaxPeriodDateFrom(currentDay);
        setMaxPeriodDateTo(currentDay);
    };

    const onClearSearchBar = () => {
        setSearchQuery('');
        setDisabledSearch(true);
        setCleanValue(false);
        params.delete('search');
        navigate({ search: params.toString() });
    }

    const handleClearFilters = () => {
        const params = new URLSearchParams(location.search);
        setCategorieSelected('');
        setDateFrom('');
        setDateTo('');
        setDisabledPeriodTo(true);
        params.delete('categorie');
        params.delete('from');
        params.delete('to');
        params.set('page', '1');
        navigate({ search: params.toString() });
    };

    const dateProps = {
        onHandleChangePeriodFrom,
        onHandleChangePeriodTo,
        disabledPeriodFrom,
        disabledPeriodTo,
        minPeriodDateFrom,
        maxPeriodDateFrom,
        minPeriodDateTo,
        maxPeriodDateTo,
        valueDateFrom: dateFrom,
        valueDateTo: dateTo,
    };

    const categoryProps: ICategoryProps = {
        options: categoriesOptions ? categoriesOptions : [],
        numberOfArticles: activities && activities?.length === 0 ? 0 : totalItems,
        onSelect,
        selectedItem: categorieSelected,
        defaultSelectedItem: 'Todas',
        label: 'Categorías',
        variant: 'outline-green'
    };

    return (
        <>
            <SEO
                title='Actividades destacadas'
                description='Actividades destacadas realizadas por el monitor de derechos de CABA'
                type='article'
            />
            <Hero
                breadcrumbs={<Breadcrumbs />}
                title='Actividades destacadas'
                paragraph='Novedades sobre la realización de encuentros, jornadas, espacios de trabajo y otras acciones destacadas.'
                media={{ type: '', src: '', url: '', alt: '', className: '' }}
                layout='row'
                containerHeightClassName='h-full'
                backgroundClassName={{ backgroundColor: '#E3DDD8' }}
                titleClassName='text-brand-green-20'
                contentContainerClassName={'!py-[3.75rem]'}
            />
            <div className='flex flex-col items-center'>
                <ResponsiveContainer className='flex flex-col justify-center' >
                    <div className='mt-16'>
                        <SearchBar
                            placeholder='Buscar actividades'
                            onSearch={handleSearch}
                            defaultValue={searchQuery}
                            cleanValue={cleanValue}
                            disabledSearch={disabledSearch}
                            onClear={onClearSearchBar}
                        />
                        <Filters
                            dateProps={dateProps}
                            categoryProps={categoryProps}
                            onClearFilters={handleClearFilters}
                        />
                    </div>
                    {loading === 'pending' || (loading === 'succeeded' && activities === null) ? (
                        <Loading
                            size={40}
                            className='h-[300px]'
                        />
                    ) : (
                        <div id='activities'>
                            {activities && activities?.length === 0 ? (
                                <div className="text-center mt-16">No se encontraron actividades.</div>
                            ) : (
                                <ActivitiesMapper data={activities} />
                            )}
                            <Pagination
                                data={activities || []}
                                totalPages={totalPages}
                                currentPage={currentPage}
                                setCurrentPage={handlePageChange}
                                scrollTargetId='activities'
                            />
                        </div>
                    )}
                </ResponsiveContainer>
                <ContactSection />
            </div>
        </>
    )
}

export default Index